import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Calendar, Award, AlertTriangle,
  ChevronLeft, ChevronRight, Download, Filter, BarChart3, LineChart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Month names in Kazakh
const MONTH_NAMES = {
  '01': 'Қаңтар', '02': 'Ақпан', '03': 'Наурыз', '04': 'Сәуір',
  '05': 'Мамыр', '06': 'Маусым', '07': 'Шілде', '08': 'Тамыз',
  '09': 'Қыркүйек', '10': 'Қазан', '11': 'Қараша', '12': 'Желтоқсан'
};

export default function YearlyProgress({ studentId, studentName, getAuthHeaders, isOwnProfile = false }) {
  const [yearlyData, setYearlyData] = useState(null);
  const [behaviorHistory, setBehaviorHistory] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [behaviorFilter, setBehaviorFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('line');
  const [activeTab, setActiveTab] = useState('progress');

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

  useEffect(() => {
    if (studentId) {
      fetchData();
    }
  }, [studentId, selectedYear, behaviorFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Determine endpoint based on whether viewing own profile or someone else's
      const progressEndpoint = isOwnProfile 
        ? `${API}/student/yearly-progress`
        : `${API}/admin/students/${studentId}/yearly-progress`;
      
      const historyEndpoint = isOwnProfile
        ? `${API}/student/behavior-history`
        : `${API}/admin/students/${studentId}/behavior-history`;

      const [progressRes, historyRes] = await Promise.all([
        axios.get(`${progressEndpoint}?year=${selectedYear}`, { headers: getAuthHeaders() }),
        axios.get(`${historyEndpoint}?year=${selectedYear}${behaviorFilter !== 'all' ? `&behavior_type=${behaviorFilter}` : ''}`, { headers: getAuthHeaders() })
      ]);

      setYearlyData(progressRes.data);
      setBehaviorHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Деректерді жүктеу қатесі');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthStr) => {
    const monthNum = monthStr.split('-')[1];
    return MONTH_NAMES[monthNum] || monthStr;
  };

  const exportToCSV = () => {
    if (!behaviorHistory?.records) return;
    
    const headers = ['Күні', 'Ай', 'Мінез-құлық', 'Түрі', 'Ұпай', 'Мұғалім', 'Түсініктеме'];
    const rows = behaviorHistory.records.map(r => [
      r.date,
      getMonthName(r.month),
      r.behavior_name,
      r.behavior_type === 'merit' ? 'Merit' : 'Demerit',
      r.behavior_type === 'merit' ? `+${r.points}` : `-${r.points}`,
      r.teacher_name,
      r.comment || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${studentName || 'student'}_behavior_history_${selectedYear}.csv`;
    link.click();
  };

  // Calculate max value for chart scaling
  const maxValue = yearlyData?.monthly_data 
    ? Math.max(...yearlyData.monthly_data.map(m => Math.max(Math.abs(m.total_score), m.merit_points, m.demerit_points)), 10)
    : 10;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">
            {isOwnProfile ? 'Менің жылдық прогресім' : `${yearlyData?.student_name || studentName} - Жылдық прогрес`}
          </h2>
          <p className="text-muted-foreground">
            {yearlyData?.class_name && `${yearlyData.class_name} сыныбы`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[120px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Yearly Summary Cards */}
      {yearlyData?.yearly_totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="text-sm opacity-90">Жалпы ұпай</div>
              <div className="text-3xl font-bold">{yearlyData.yearly_totals.total_score}</div>
              <div className="text-xs opacity-75">{selectedYear} жыл</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="text-sm opacity-90">Merit</div>
              <div className="text-3xl font-bold">+{yearlyData.yearly_totals.total_merit_points}</div>
              <div className="text-xs opacity-75">{yearlyData.yearly_totals.total_merit_count} рет</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <div className="text-sm opacity-90">Demerit</div>
              <div className="text-3xl font-bold">-{yearlyData.yearly_totals.total_demerit_points}</div>
              <div className="text-xs opacity-75">{yearlyData.yearly_totals.total_demerit_count} рет</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="text-sm opacity-90">Ең жақсы ай</div>
              <div className="text-xl font-bold">
                {yearlyData.yearly_totals.best_month ? getMonthName(yearlyData.yearly_totals.best_month) : '-'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Progress Chart and History */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="progress" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Жылдық график
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Толық тарих
          </TabsTrigger>
        </TabsList>

        {/* Progress Chart Tab */}
        <TabsContent value="progress" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle>Ай сайынғы прогрес</CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant={chartType === 'line' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setChartType('line')}
                >
                  <LineChart className="w-4 h-4" />
                </Button>
                <Button 
                  variant={chartType === 'bar' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setChartType('bar')}
                >
                  <BarChart3 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Custom Chart */}
              <div className="h-[300px] relative">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-8 w-12 flex flex-col justify-between text-xs text-muted-foreground">
                  <span>{maxValue}</span>
                  <span>{Math.round(maxValue / 2)}</span>
                  <span>0</span>
                  <span>-{Math.round(maxValue / 2)}</span>
                  <span>-{maxValue}</span>
                </div>
                
                {/* Chart area */}
                <div className="ml-14 h-full relative">
                  {/* Zero line */}
                  <div className="absolute left-0 right-0 top-1/2 border-t border-dashed border-gray-300"></div>
                  
                  {/* Grid lines */}
                  <div className="absolute left-0 right-0 top-0 border-t border-gray-100"></div>
                  <div className="absolute left-0 right-0 top-1/4 border-t border-gray-100"></div>
                  <div className="absolute left-0 right-0 top-3/4 border-t border-gray-100"></div>
                  <div className="absolute left-0 right-0 bottom-8 border-t border-gray-100"></div>
                  
                  {/* Bars or Line */}
                  <div className="flex items-end justify-between h-[calc(100%-2rem)] px-1">
                    {yearlyData?.monthly_data?.map((month, idx) => {
                      const height = Math.abs(month.total_score) / maxValue * 45;
                      const isPositive = month.total_score >= 0;
                      
                      if (chartType === 'bar') {
                        return (
                          <div key={month.month} className="flex flex-col items-center flex-1 relative h-full">
                            {/* Bar */}
                            <div className="absolute left-1/2 -translate-x-1/2 w-4/5 flex flex-col items-center" 
                                 style={{ top: isPositive ? `${50 - height}%` : '50%' }}>
                              <div 
                                className={`w-full rounded-sm transition-all ${
                                  isPositive ? 'bg-green-500' : 'bg-red-500'
                                }`}
                                style={{ height: `${height * 2}%`, minHeight: month.total_score !== 0 ? '4px' : '0' }}
                              />
                            </div>
                            {/* Label */}
                            <span className="absolute bottom-0 text-[10px] text-muted-foreground">
                              {getMonthName(month.month).slice(0, 3)}
                            </span>
                          </div>
                        );
                      } else {
                        // Line chart points
                        const y = 50 - (month.total_score / maxValue * 45);
                        return (
                          <div key={month.month} className="flex flex-col items-center flex-1 relative h-full group">
                            {/* Point */}
                            <div 
                              className="absolute w-3 h-3 rounded-full bg-primary border-2 border-white shadow-md z-10 transition-transform group-hover:scale-150"
                              style={{ top: `${y}%`, transform: 'translateY(-50%)' }}
                            />
                            {/* Hover tooltip */}
                            <div className="absolute opacity-0 group-hover:opacity-100 bg-black text-white text-xs px-2 py-1 rounded pointer-events-none z-20 whitespace-nowrap"
                                 style={{ top: `${y - 15}%` }}>
                              {month.total_score} ұпай
                            </div>
                            {/* Label */}
                            <span className="absolute bottom-0 text-[10px] text-muted-foreground">
                              {getMonthName(month.month).slice(0, 3)}
                            </span>
                          </div>
                        );
                      }
                    })}
                  </div>
                  
                  {/* Line connecting points (for line chart) */}
                  {chartType === 'line' && yearlyData?.monthly_data && (
                    <svg 
                      className="absolute inset-0 ml-0 overflow-visible pointer-events-none" 
                      style={{ top: 0, height: 'calc(100% - 2rem)' }}
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      <polyline
                        fill="none"
                        stroke="hsl(var(--primary))"
                        strokeWidth="0.5"
                        vectorEffect="non-scaling-stroke"
                        points={yearlyData.monthly_data.map((month, idx) => {
                          const x = (idx + 0.5) * (100 / 12);
                          const y = 50 - (month.total_score / maxValue * 45);
                          return `${x},${y}`;
                        }).join(' ')}
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Legend */}
              <div className="flex justify-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span>Merit (+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span>Demerit (-)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Breakdown Table */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg">Ай сайынғы бөлшектер</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Ай</th>
                      <th className="text-center py-2 px-3">Merit</th>
                      <th className="text-center py-2 px-3">Demerit</th>
                      <th className="text-center py-2 px-3">Жалпы</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyData?.monthly_data?.filter(m => m.merit_count > 0 || m.demerit_count > 0).map(month => (
                      <tr key={month.month} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-3 font-medium">{getMonthName(month.month)}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="text-green-600">+{month.merit_points}</span>
                          <span className="text-xs text-muted-foreground ml-1">({month.merit_count})</span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className="text-red-600">-{month.demerit_points}</span>
                          <span className="text-xs text-muted-foreground ml-1">({month.demerit_count})</span>
                        </td>
                        <td className="py-2 px-3 text-center font-bold">
                          <span className={month.total_score >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {month.total_score >= 0 ? '+' : ''}{month.total_score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Мінез-құлық тарихы</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={behaviorFilter} onValueChange={setBehaviorFilter}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Барлығы</SelectItem>
                    <SelectItem value="merit">Merit</SelectItem>
                    <SelectItem value="demerit">Demerit</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={exportToCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {behaviorHistory?.records?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Бұл кезеңде жазба жоқ
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {behaviorHistory?.records?.map((record, idx) => (
                    <div 
                      key={record.id || idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        record.behavior_type === 'merit' 
                          ? 'bg-green-50 border-green-200 dark:bg-green-950/20' 
                          : 'bg-red-50 border-red-200 dark:bg-red-950/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          record.behavior_type === 'merit' ? 'bg-green-500' : 'bg-red-500'
                        } text-white`}>
                          {record.behavior_type === 'merit' ? (
                            <Award className="w-5 h-5" />
                          ) : (
                            <AlertTriangle className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{record.behavior_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {record.date} • {record.teacher_name}
                          </div>
                          {record.comment && (
                            <div className="text-xs text-muted-foreground mt-1 italic">
                              "{record.comment}"
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge variant={record.behavior_type === 'merit' ? 'default' : 'destructive'}>
                        {record.behavior_type === 'merit' ? '+' : '-'}{record.points}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              {behaviorHistory?.total_records > 0 && (
                <div className="text-center text-sm text-muted-foreground mt-4">
                  Барлығы: {behaviorHistory.total_records} жазба
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
